import type { EngineId, LanguageId, RecognitionModeId } from './constants';
import type { VariantId } from './imageOps';
import type { LayoutTable, OcrToken } from './layout';

export interface OcrBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface OcrLine {
  text: string;
  /** 0-100, or null when the engine does not report per-line confidence. */
  confidence: number | null;
  bbox?: OcrBox | null;
  /** Detection polygon [[x,y], ...] when the engine reports one. */
  poly?: number[][] | null;
  pageNumber?: number;
}

export interface OcrResult {
  text: string;
  lines: OcrLine[];
  /** Positioned word/line tokens kept for layout reconstruction and debugging. */
  tokens: OcrToken[];
  tables: LayoutTable[];
  /** 0-100, or null when unavailable. */
  averageConfidence: number | null;
  /** The recogniser that actually produced this result. */
  engine: EngineId;
  engineLabel: string;
  /** The model/language configuration that was actually loaded. */
  languageId: LanguageId;
  languageConfig: string[];
  modelSource: string;
  mode: RecognitionModeId;
  /** Preprocessing variant that won the scoring comparison. */
  preprocessVariant: VariantId;
  segmentationMode: string;
  durationMs: number;
  pageNumber: number | null;
  invertedForDarkBackground: boolean;
  /** Median glyph height measured back in original image pixels. */
  medianSourceTextHeight: number | null;
  lowResolution: boolean;
  warnings: string[];
}

export type OcrErrorCode =
  | 'UNSUPPORTED_FORMAT'
  | 'INVALID_SIGNATURE'
  | 'FILE_TOO_LARGE'
  | 'IMAGE_TOO_LARGE'
  | 'TOO_MANY_FILES'
  | 'TOO_MANY_PAGES'
  | 'EMPTY_FILE'
  | 'EMPTY_IMAGE'
  | 'DECODE_FAILED'
  | 'CORRUPT_FILE'
  | 'NO_TEXT_FOUND'
  | 'MODEL_DOWNLOAD_FAILED'
  | 'WORKER_INIT_FAILED'
  | 'ENGINE_UNAVAILABLE'
  | 'MEMORY_PRESSURE'
  | 'TIMEOUT'
  | 'CANCELLED'
  | 'STALE_RUN'
  | 'ITEM_REMOVED'
  | 'PDF_PARSE_FAILED'
  | 'PAGE_FAILED'
  | 'UNKNOWN';

export class OcrError extends Error {
  code: OcrErrorCode;

  userMessage: string;

  /** Engine the failed attempt belonged to, so the UI can offer a real fallback. */
  engine?: EngineId;

  constructor(code: OcrErrorCode, userMessage: string, technical?: string, engine?: EngineId) {
    super(technical || userMessage);
    this.name = 'OcrError';
    this.code = code;
    this.userMessage = userMessage;
    this.engine = engine;
  }
}

export function toOcrError(err: unknown, fallbackCode: OcrErrorCode = 'UNKNOWN'): OcrError {
  if (err instanceof OcrError) return err;
  const raw = typeof err === 'string' ? err : (err as Error)?.message || String(err);
  const lower = raw.toLowerCase();
  if (lower.includes('abort') || lower.includes('cancel')) {
    return new OcrError('CANCELLED', 'Recognition was cancelled.', raw);
  }
  if (lower.includes('network') || lower.includes('fetch') || lower.includes('traineddata')) {
    return new OcrError(
      'MODEL_DOWNLOAD_FAILED',
      'The recognition model could not be loaded. Check your connection and retry.',
      raw,
    );
  }
  if (lower.includes('memory') || lower.includes('allocation')) {
    return new OcrError(
      'MEMORY_PRESSURE',
      'The browser ran out of memory for this image. Try a smaller image or fewer files at once.',
      raw,
    );
  }
  return new OcrError(fallbackCode, 'Recognition failed. Please retry.', raw);
}

/**
 * Adapter contract shared by every recognition backend. A self-hosted
 * PaddleOCR server could be added later as another implementation of this
 * interface without touching the UI layer.
 */
export interface OcrEngineAdapter {
  id: EngineId;
  label: string;
  supportsLanguage(languageId: LanguageId): boolean;
  prepare(ctx: OcrRunContext, onStatus?: StatusCallback): Promise<void>;
  recognize(canvas: HTMLCanvasElement, ctx: OcrRunContext, onStatus?: StatusCallback): Promise<OcrResult>;
  dispose(): Promise<void>;
}

export interface OcrRunContext {
  languageId: LanguageId;
  mode: RecognitionModeId;
  /** Monotonic token; a run whose generation is stale must be discarded. */
  generation: number;
  isStale: () => boolean;
  pageNumber?: number | null;
  invertedForDarkBackground?: boolean;
}

export type StatusCallback = (status: { text: string; progress: number }) => void;

export function averageOf(values: number[]): number | null {
  const usable = values.filter((v) => Number.isFinite(v));
  if (!usable.length) return null;
  return usable.reduce((a, b) => a + b, 0) / usable.length;
}
